<template>
  <v-form ref="form" @submit.prevent="submitHandler">
    <v-card class="task-form">
      <v-toolbar
        color="primary"
        dark
        flat
        class="panel-header"
      >
        <v-toolbar-title>{{ title }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-text-field
          v-model="task.title"
          placeholder="Add Title"
          :rules="[rules.required]"
          dense
          filled
          class="task-title v-text-field--no-line"
        />
        <v-btn-toggle
          v-model="task.type"
          color="primary"
          group
          mandatory
          class="task-type-picker"
        >
          <v-btn
            :small="$vuetify.breakpoint.xs"
            :value="taskTypesEnum.call"
            class="ml-0"
            :class="$vuetify.breakpoint.xs ? 'mr-0 px-2' : 'mr-2'"
          >
            <v-icon :x-small="$vuetify.breakpoint.xs" icon>mdi-phone-classic</v-icon>
            {{ taskTypesLabels[taskTypesEnum.call] }}
          </v-btn>
          <v-btn
            :small="$vuetify.breakpoint.xs"
            :value="taskTypesEnum.email"
            class="ml-0"
            :class="$vuetify.breakpoint.xs ? 'mr-0 px-2' : 'mr-2'"
          >
            <v-icon :x-small="$vuetify.breakpoint.xs" icon>mdi-at</v-icon>
            {{ taskTypesLabels[taskTypesEnum.email] }}
          </v-btn>
          <v-btn
            :small="$vuetify.breakpoint.xs"
            :value="taskTypesEnum.todo"
            class="ml-0"
            :class="$vuetify.breakpoint.xs ? 'mr-0 px-2' : 'mr-2'"
          >
            <v-icon :x-small="$vuetify.breakpoint.xs" icon>mdi-format-list-checks</v-icon>
            {{ taskTypesLabels[taskTypesEnum.todo] }}
          </v-btn>
          <v-btn
            :small="$vuetify.breakpoint.xs"
            :value="taskTypesEnum.meeting"
            class="ml-0"
            :class="$vuetify.breakpoint.xs ? 'mr-0 px-2' : 'mr-2'"
          >
            <v-icon :x-small="$vuetify.breakpoint.xs" icon>mdi-calendar-outline</v-icon>
            {{ taskTypesLabels[taskTypesEnum.meeting] }}
          </v-btn>
        </v-btn-toggle>
        <v-divider class="my-2" />
        <v-row class="align-center">
          <v-col cols="1">
            <v-icon icon>mdi-clipboard-account</v-icon>
          </v-col>
          <v-col class="d-flex align-center">
            <v-btn
              large
              outlined
              :color="assignedToMe ? 'primary' : 'grey'"
              class="mr-2"
              data-track="task.form.assign-to-me"
              @click="assignToMe"
            >
              <v-icon v-if="assignedToMe" icon>mdi-check</v-icon>
              My Task
            </v-btn>
            <v-btn
              v-if="assignedToMe"
              large
              outlined
              color="grey"
              @click="assignedToMe = false"
            >
              Assignee
            </v-btn>
            <v-autocomplete
              v-else
              ref="assigned_to"
              v-model="task.assignee"
              :items="members"
              :loading="membersLoading"
              label="Assigned to"
              item-text="full_name"
              item-value="id"
              :rules="[rules.assignedTo]"
              autocomplete="off"
              hide-details
              class="assign-to-member"
            >
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.profile.first_name }} <strong>{{ item.profile.last_name }}</strong></v-list-item-title>
                  <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-checkbox
              v-model="task.marketing"
              label="Marketing"
              hide-details
            >
              <template #append>
                <v-tooltip left bottom max-width="250">
                  <template #activator="{ on, attrs }">
                    <v-icon v-bind="attrs" class="cursor-pointer" v-on="on">mdi-information-outline</v-icon>
                  </template>
                  <span><b>Only for the Marketing Development Team.</b> This adds a task for the marketing team</span>
                </v-tooltip>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
        <v-row class="align-center mt-3" no-gutters>
          <v-col cols="1">
            <v-icon icon>mdi-exclamation</v-icon>
          </v-col>
          <v-col cols="11">
            <div class="d-flex align-center">
              Priority: <PriorityField v-model="task.priority" />
            </div>
          </v-col>
        </v-row>
        <v-row v-if="!isRecurrent" class="align-center mt-0">
          <v-col cols="1">
            <v-icon icon>mdi-clock-outline</v-icon>
          </v-col>
          <v-col cols="11">
            <v-row>
              <v-col cols="6">
                <CommonDatePicker
                  label="Due date"
                  :enter-date="dueDate"
                  :disabled="false"
                  :rules="[rules.required]"
                  outlined
                  hide-details
                  dense
                  @updateDate="dueDate = $event"
                />
              </v-col>
              <v-col cols="6" md="3">
                <v-select
                  v-model="dueDateTime"
                  menu-props="auto"
                  :items="times"
                  outlined
                  hide-details
                  dense
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="align-center">
          <v-col cols="1" class="pt-0 pb-0">
            <v-icon icon>mdi-update</v-icon>
          </v-col>
          <v-col cols="11" class="pt-0 pb-0">
            <TaskSchedule
              ref="taskSchedule"
              :is-recurrent.sync="isRecurrent"
              :disable-recurrence-edit="!!taskEntry"
              :rrule-entry="task.schedule"
              @submit="scheduleSubmitHandler"
            />
          </v-col>
        </v-row>
        <v-row class="align-start">
          <v-col cols="1">
            <v-icon icon>mdi-text</v-icon>
          </v-col>
          <v-col cols="11">
            <QuillEditor
              id="task-description"
              v-model="task.description"
              class="editor"
              placeholder="Enter a description..."
              :toolbar-options="quillToolbarExtended"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="1">
            <v-icon icon>mdi-format-list-numbered</v-icon>
          </v-col>
          <v-col cols="11">
            <div>
              <v-row class="align-center">
                <v-col v-if="relatedRecords.length">
                  <span v-for="item in relatedRecords" :key="item._id">
                    <CommonChip
                      class="mr-1 mb-1"
                      close
                      :small="false"
                      :to="getRelatedRecordRoute(item)"
                      target="_blank"
                      :large="item.type === 'contact' && !!item.company"
                      @click:close="remove(item._id)"
                    >
                      <v-avatar left>
                        <v-icon small>{{ generateAssociatedLogo(item) }}</v-icon>
                      </v-avatar>
                      <AssociatedItem :item="item" />
                    </CommonChip>
                  </span>
                </v-col>
                <v-col v-if="!associatedDialog">
                  <v-btn
                    outlined
                    color="grey darken-1"
                    rounded
                    small
                    data-track="task.form-associated-record.open"
                    @click="associatedDialog = true"
                  >
                    <v-icon icon>mdi-plus</v-icon>Add Associated Record
                  </v-btn>
                </v-col>
              </v-row>
              <div v-if="associatedDialog">
                <AssociatedForm
                  :collection="relatedRecords"
                  source="task"
                  @addAssociated="addAssociatedHandler"
                  @close="associatedDialog = false"
                />
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row class="align-start">
          <v-col cols="1">
            <v-icon icon>mdi-account-multiple</v-icon>
          </v-col>
          <v-col cols="11">
            <CollaboratorsSelect
              v-model="task.collaborators"
              :members="members"
              :loading="membersLoading"
            />
          </v-col>
        </v-row>
        <v-row class="align-start">
          <v-col cols="1">
            <v-icon icon>mdi-account-group</v-icon>
          </v-col>
          <v-col cols="11">
            <TeamsSelect v-model="task.team_ids" />
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions class="justify-end">
        <v-btn
          v-if="taskEntry"
          text
          color="red"
          :disabled="saveLoading"
          data-track="task.record.archive"
          @click="$emit('archive')"
        >
          Archive
        </v-btn>
        <v-spacer />
        <v-btn text :disabled="saveLoading" @click="$emit('close')">
          Close
        </v-btn>
        <v-btn
          color="primary"
          type="submit"
          :loading="saveLoading"
          data-track="task.form.save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script lang="ts">
import { PropType } from 'vue';
import { mapState } from 'vuex';
import { DateTime } from 'luxon';
import mixins from 'vue-typed-mixins';
import { RRule } from 'rrule';

import { TaskCreateUpdateDTO } from '@/bundles/Tasks/dto/TaskCreateUpdateDTO';
import { ITextValue, VForm } from '@/types/common';
import { getTaskRelatedRecordRoute } from '@/bundles/App/helpers';
import { Nullable } from '@/utils/types';
import { ITask } from '@/bundles/Tasks/interfaces/ITask';
import { TaskTypesEnum, TaskTypesLabels } from '@/bundles/Tasks/enums/TaskTypesEnum';
import { memberService } from '@/bundles/Members/factory/memberServiceFactory';
import { getTypeLogo } from '@/bundles/Tasks/helpers/helpers';
import { VueEditorMixin } from '@/bundles/App/mixins/editor/VueEditorMixin';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';

import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import AssociatedForm from '@/bundles/Tasks/components/associated/AssociatedForm.vue';
import CommonDatePicker from '@/components/common-components/pickers/CommonDatePicker.vue';
import AssociatedItem from '@/bundles/Tasks/components/associated/AssociatedItem.vue';
import CollaboratorsSelect from '@/bundles/Tasks/components/CollaboratorsSelect.vue';
import TeamsSelect from '@/bundles/Tasks/components/TeamsSelect.vue';
import QuillEditor from '@/bundles/Account/components/QuillEditor.vue';
import PriorityField from '@/bundles/Common/components/PriorityField.vue';
import TaskSchedule from '@/bundles/Tasks/components/TaskSchedule.vue';

interface ITaskForm {
  task: TaskCreateUpdateDTO;
  rules: {
    required: (v: any) => boolean|string,
    assignedTo: (v: any) => boolean|string,
  };
  assignedToMe: boolean;
  members: any[]; // todo: add type
  membersLoading: boolean;
  associatedDialog: boolean;
  relatedRecords: any[];
  isRecurrent: boolean;
}

export default mixins(VueEditorMixin, AccountLayoutMixin).extend({
  name: 'BaseTaskForm',

  components: {
    TaskSchedule,
    PriorityField,
    QuillEditor,
    CollaboratorsSelect,
    TeamsSelect,
    AssociatedItem,
    CommonDatePicker,
    AssociatedForm,
    CommonChip,
  },

  props: {
    taskEntry: {
      type: Object as PropType<Nullable<ITask>>,
      default: null
    },
    collectionType: {
      type: String,
      default: null
    },
    associatedRecords: {
      type: Array,
      default: () => []
    },
    saveLoading: Boolean,
    title: {
      type: String,
      default: 'Create Task'
    },
    initialType: {
      type: String as PropType<TaskTypesEnum>,
      default: null
    },
    initialMarketing: Boolean,
  },

  data: (): ITaskForm => ({
    task: new TaskCreateUpdateDTO(),
    rules: {
      required: (value) => !!value || 'This field is required',
      assignedTo: (value) => !!value || 'You must assign this task',
    },
    assignedToMe: true,
    members: [],
    membersLoading: false,
    associatedDialog: false,
    relatedRecords: [],
    isRecurrent: false,
  }),

  computed: {
    ...mapState({
      token: (state: any) => state.idToken,
      userId: (state: any) => state.userId
    }),
    taskTypesEnum (): typeof TaskTypesEnum {
      return TaskTypesEnum;
    },
    taskTypesLabels (): typeof TaskTypesLabels {
      return TaskTypesLabels;
    },
    times (): ITextValue<string>[] {
      const hours: ITextValue<string>[] = [];

      for (let hour = 0; hour < 24; hour++) {
        hours.push(this.getTimeItem({ hour }));
        hours.push(this.getTimeItem({ hour, minute: 15 }));
        hours.push(this.getTimeItem({ hour, minute: 30 }));
        hours.push(this.getTimeItem({ hour, minute: 45 }));
      }

      return hours;
    },
    dueDate: {
      get (): string {
        return DateTime.fromJSDate(this.task.due_date).toISODate(); // local date in ISO format without time "2025-01-12"
      },
      set (value: string) {
        // Parses as local date
        const newDate = DateTime.fromISO(value);
        const existingTime = DateTime.fromJSDate(this.task.due_date);

        this.task.due_date = newDate
          .set({ hour: existingTime.hour, minute: existingTime.minute, second: 0, millisecond: 0 })
          .toJSDate();
      }
    },
    dueDateTime: {
      get (): string {
        return DateTime.fromJSDate(this.task.due_date).toFormat('HH:mm');
      },
      set (value: string) {
        const [hours, minutes] = value.split(':').map(Number);
        const date = DateTime.fromJSDate(this.task.due_date);

        this.task.due_date = date
          .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
          .toJSDate();
      }
    }
  },

  watch: {
    assignedToMe: {
      handler (value: boolean) {
        if (value) {
          this.task.assignee = +this.userId;
        }
      },
      immediate: true,
    },
    associatedRecords: {
      handler (value: any[]) {
        this.relatedRecords = value.map((record: any) => ({
          ...record,
          type: this.collectionType || record.type
        }));
      },
      immediate: true,
    },
  },

  created () {
    this.fetchMembers();

    if (this.initialType && !this.taskEntry) {
      this.task.type = this.initialType;
    }

    if (this.initialMarketing && !this.taskEntry) {
      this.task.marketing = true;
    }

    if (this.taskEntry) {
      this.editTaskHandler(this.taskEntry);
    }
  },

  methods: {
    assignToMe () {
      this.task.assignee = +this.userId;
      this.assignedToMe = true;
    },
    async fetchMembers () {
      try {
        this.membersLoading = true;

        const params = [
          { name: 'status', value: 'active' },
          { name: 'take', value: 0, } // zero used to return all members
        ];
        const { data: members } = await memberService.list(params);
        this.members = members.map((member) => ({
          full_name: `${member.profile.first_name} ${member.profile.last_name}`,
          ...member,
        }));
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.membersLoading = false;
      }
    },
    addAssociatedHandler (items: any[]) {
      items.forEach(item => {
        const index = this.relatedRecords.findIndex(record => record._key === item._key);

        if (index === -1) {
          this.relatedRecords.push(item);
        } else {
          this.relatedRecords[index] = item;
        }
      });
    },
    remove (id: string) {
      this.relatedRecords = this.relatedRecords.filter((item: any) => {
        return item._id !== id;
      });
    },
    generateAssociatedLogo (item): string {
      return getTypeLogo(item.type);
    },
    submitHandler () {
      if (!(this.$refs.taskSchedule as InstanceType<typeof TaskSchedule>).flushPendingSchedule()) {
        return;
      }

      if (!(this.$refs.form as VForm).validate()) {
        return;
      }

      const relatedRecords = this.relatedRecords.map((item: any) => ({
        _id: item._id,
        type: item.type,
      }));

      this.$emit('submit', new TaskCreateUpdateDTO({ ...this.task, related_records: relatedRecords }).toJSON());
    },
    editTaskHandler (value: ITask) {
      this.task = new TaskCreateUpdateDTO({ ...value, related_records: [] });

      this.assignedToMe = value.assignee === +this.userId;
      this.relatedRecords = value.collections;
      this.isRecurrent = !!value.recurrent_group_id;
    },
    getTimeItem ({ hour, minute }: { hour: number, minute?: number }): ITextValue<string> {
      return {
        text: DateTime.fromObject({ hour, minute }).toLocaleString(DateTime.TIME_SIMPLE),
        value: DateTime.fromObject({ hour, minute }).toLocaleString(DateTime.TIME_24_SIMPLE)
      };
    },
    getRelatedRecordRoute (relatedRecord: any) {
      return this.getAccountRoute(getTaskRelatedRecordRoute(relatedRecord));
    },
    scheduleSubmitHandler ({ rrule, dateEnd, time }: { rrule: Nullable<RRule>; dateEnd?: string; time?: string; }) {
      if (rrule && dateEnd && time) {
        this.task = Object.assign({}, this.task, { schedule: rrule.toString() });
        this.dueDate = dateEnd;
        this.dueDateTime = time;
        return;
      }

      delete this.task.schedule;
      this.task = Object.assign({}, this.task); // trigger reactivity
    }
  }
});
</script>

<style scoped lang="scss">
.task-type-picker {
  button {
    border-radius: 5px!important;
    margin-left: 0;
  }
  .v-icon {
    margin-right: 10px;
  }
}
</style>
